<template>
  <v-card class="d-flex align-content-stretch flex-grow-1" data-testid="accountCard">
    <v-container>
      <v-row class="d-flex align-content-stretch flex-grow-1">
        <v-col cols="7" class="insured-details pa-6 pb-0 pr-3 flex-grow-1" :class="{'mb-5' : module !== 'account'}">
          <h3 class="mb-3 text-bold">Insured Name &amp; Mailing Address</h3>
          <p class="mb-1" data-testid="insuredNameText">{{selectedInsured.Name}}</p>
          <span v-if="selectedInsured.Street1">
            <p class="mb-1" data-testid="insuredAddressLine1Text">{{selectedInsured.Street1}} {{selectedInsured.Street2}}</p>
            <p class="mb-1" data-testid="insuredAddressLine2Text">{{selectedInsured.City}}, {{selectedInsured.State}} {{selectedInsured.Postal}} {{selectedInsured.Country}}</p>
          </span>
          <span v-if="insuredEmailOptions.InsuredEmailAddress && !editEmail && module === 'account'">
            <p class="mb-4">
              <a :href="'mailto:' + insuredEmailOptions.InsuredEmailAddress" class="blue-link" title="The insured is currently receiving policy communications by email." data-testid="insuredEmailLink">
                {{insuredEmailOptions.InsuredEmailAddress}}
              </a>
              <v-btn v-if="canEditEmail" icon @click="editEmail = true" class="mb-1" title="Edit Email" data-testid="editEmailButton">
                <i class="fas fa-pen" style="font-size: 18px;"></i>
              </v-btn>
            </p>
          </span>
          <span v-if="showEditOption && !insuredEmailOptions.InsuredEmailAddress && !editEmail && module === 'account' && canEditEmail">
            <p class="mb-5 blue-link" @click="editEmail = true" data-testid="addEmailLink">+ add email</p>
          </span>
        </v-col>
        <v-col cols="5" class="risk-details pa-6 pb-0 pl-3 flex-grow-1">
          <h3 class="mb-3 text-bold">Risk Address</h3>
          <span>
            <p class="mb-1" data-testid="riskAddressLine1Text">{{selectedRisk.Street1}} {{selectedRisk.Street2}}</p>
            <p class="mb-1" data-testid="riskAddressLine2Text">{{selectedRisk.City}}, {{selectedRisk.State}} {{selectedRisk.Postal}} {{selectedRisk.Country}}</p>
            <p class="mb-1" v-if="selectedRisk.County" data-testid="riskCountyText">
              {{selectedRisk.County + (selectedRisk.County ? ' County' : '')}}
            </p>
          </span>
        </v-col>
      </v-row>

      <v-row v-if="editEmail">
        <v-col class="pl-6 pb-4" cols="5">
          <div data-testid="insuredEmailInputField">
            <TextInput
                label="Email Address"
                v-model="insuredEmailOptions.InsuredEmailAddress"
                :errors="emailInputError"
                @change="val => (insuredEmailOptions.InsuredEmailAddress) = val"/>
          </div>
        </v-col>
        <v-col cols="4" class="action-buttons">
          <v-btn color="secondary" @click="saveInsuredEmailOptions" data-testid="saveEmailButton">Save</v-btn>
          <v-btn class="px-2 ml-4" color="white" @click="cancelEditEmail" data-testid="cancelEmailButton">Cancel</v-btn>
        </v-col>
        <v-col v-if="selectedInsuredEmailOptions.InsuredEmailAddress" cols="3" class="unsubscribe">
          <b @click="unsubscribeModal = true" class="blue-link" data-testid="unsubscribeLink">Unsubscribe</b>
        </v-col>
      </v-row>
      <v-row v-if="editEmail" class="px-6 pb-6" data-testid="emailAttestationField">
        <Checkbox
          :label="attestationText"
          v-model="insuredEmailOptions.InsuredReceivesPolicyEmails"
          :errors="attestationError"
          @change="val => (insuredEmailOptions.InsuredReceivesPolicyEmails) = val"/>
      </v-row>
      <Dialog :value="unsubscribeModal" title="Unsubscribe" @close-dialog="unsubscribeModal = false" data-testid="unsubscribeModal">

        <template v-slot:card-text>
          <div cols="12">
            <p>By unsubscribing, I confirm the insured has declined consent to receive emails about their policy, such as invoices, endorsement documents, or any notices of cancellation.</p>
            <p>This action will remove the email address associated with this account.</p>
          </div>
        </template>

        <template v-slot:card-actions>
          <v-btn color="secondary" type="button" outlined @click="unsubscribeModal = false" data-testid="unsubscribeCancelButton">
            Cancel
          </v-btn>
          <v-btn color="secondary" type="button" @click="confirmUnsubscribe" data-testid="unsubscribeConfirmButton">
            Unsubscribe
          </v-btn>
        </template>

      </Dialog>
    </v-container>
  </v-card>
</template>

<script>
import TextInput from '../form/TextInput.vue';
import Checkbox from '../form/Checkbox.vue';
import Dialog from './Dialog.vue';
import { validateEmail } from '@/scripts/helper';
import { PolicyService } from '@/api/PolicyService';

export default {
  name: 'AccountCard',
  components: {
    TextInput,
    Checkbox,
    Dialog
  },
  data () {
    return {
      editEmail: false,
      unsubscribeModal: false,
      insuredEmailOptions: {
        InsuredReceivesPolicyEmails: false,
        InsuredEmailAddress: null,
        InsuredEmailAttestationText: null
      },
      attestationText: 'The insured entity has granted consent to receiving emails about their policy, such as invoices, endorsement documents, or any notices of cancellation.',
      attestationError: null,
      emailInputError: null,
      canEditEmail: false
    };
  },
  props: {
    account: {
      type: Object,
      default: () => {}
    },
    isFloodProduct: {
      type: Boolean,
      default: false
    },
    isV2SalesChannel: {
      type: Boolean,
      default: false
    },
    module: {
      type: String,
      default: null
    },
    policyTerms: {
      type: Array,
      default: null
    },
    policy: {
      type: Object
    }
  },
  computed: {
    selectedInsured () {
      if (this.account && this.account.Insured) return this.account.Insured;
      return {};
    },
    selectedRisk () {
      if (this.account && this.account.Risk) return this.account.Risk;
      return {};
    },
    selectedPolicyTermStatus () {
      if (this.policyTerms && this.policyTerms.length > 0) return this.policyTerms[0].Status;
      return '';
    },
    selectedInsuredEmailOptions () {
      if (this.policy?.Policy?.Insured?.InsuredEmailOptions) { return this.policy.Policy.Insured.InsuredEmailOptions; }
      return {};
    },
    showEditOption () {
      return this.isFloodProduct || this.isV2SalesChannel;
    }
  },
  methods: {
    async saveInsuredEmailOptions () {
      this.emailInputError = null;
      this.attestationError = null;

      if (!validateEmail(this.insuredEmailOptions.InsuredEmailAddress)) {
        this.emailInputError = ['A valid email is required'];
      }

      if (!this.insuredEmailOptions.InsuredReceivesPolicyEmails) {
        this.attestationError = [''];
      } else {
        this.insuredEmailOptions.InsuredEmailAttestationText = this.attestationText;
      }

      if (this.emailInputError === null && this.attestationError === null) {
        await PolicyService.updatePolicyEmailPreferences({ body: {
          UpdatedEmailOptions: this.insuredEmailOptions,
          PolicyId: this.policy.id,
          SubmissionId: this.account.TermList[0].SubmissionIdGuid,
          SubmissionNumber: this.account.TermList[0].SubmissionId
        } }).then(() => {
            this.$store.commit('updateFormControl', {
                module: 'account',
                name: 'InsuredEmailOptions',
                value: this.insuredEmailOptions
            });
            this.editEmail = false;
            this.policy.Policy.Insured.InsuredEmailOptions = {
              InsuredEmailAddress: this.insuredEmailOptions.InsuredEmailAddress,
              InsuredEmailAttestationText: this.insuredEmailOptions.InsuredEmailAttestationText,
              InsuredReceivesPolicyEmails: this.insuredEmailOptions.InsuredReceivesPolicyEmails
            };
          })
          .catch(() => {
              this.$store.dispatch('toastError', { Message: 'Failed to update email preferences' });
          });
      }
    },
    cancelEditEmail () {
      this.editEmail = false;
      this.insuredEmailOptions = {
        InsuredEmailAddress: this.selectedInsuredEmailOptions.InsuredEmailAddress,
        InsuredEmailAttestationText: this.selectedInsuredEmailOptions.InsuredEmailAttestationText,
        InsuredReceivesPolicyEmails: this.selectedInsuredEmailOptions.InsuredReceivesPolicyEmails
      };
    },
    async confirmUnsubscribe () {
      this.insuredEmailOptions = {
        InsuredReceivesPolicyEmails: false,
        InsuredEmailAddress: null,
        InsuredEmailAttestationText: null
      };

      await PolicyService.updatePolicyEmailPreferences({ body: {
        UpdatedEmailOptions: this.insuredEmailOptions,
        PolicyId: this.policy.id,
        SubmissionId: this.account.TermList[0].SubmissionIdGuid,
        SubmissionNumber: this.account.TermList[0].SubmissionId
      } }).then(() => {
          this.unsubscribeModal = false;
          this.editEmail = false;
        })
        .catch(() => {
          this.$store.dispatch('toastError', { Message: 'Failed to update email preferences' });
          this.insuredEmailOptions = {
            InsuredEmailAddress: this.selectedInsuredEmailOptions.InsuredEmailAddress,
            InsuredEmailAttestationText: this.selectedInsuredEmailOptions.InsuredEmailAttestationText,
            InsuredReceivesPolicyEmails: this.selectedInsuredEmailOptions.InsuredReceivesPolicyEmails
          };
        });
    }
  },
  watch: {
    insuredEmailOptions: {
      deep: true,
      handler (newVal) {
        if (newVal.InsuredReceivesPolicyEmails) {
          this.insuredEmailOptions.InsuredEmailAttestationText = this.attestationText;
        }

        if (!newVal.InsuredReceivesPolicyEmails) {
          this.insuredEmailOptions.InsuredEmailAttestationText = null;
        }
      }
    },
    selectedPolicyTermStatus: {
      immediate: true,
      async handler (newVal) {
        if (newVal === 'Issued') {
            this.insuredEmailOptions = {
              InsuredEmailAddress: this.selectedInsuredEmailOptions.InsuredEmailAddress ?? null,
              InsuredEmailAttestationText: null,
              InsuredReceivesPolicyEmails: false
            };
            this.canEditEmail = true;
        } else {
          this.insuredEmailOptions = {
            InsuredEmailAddress: this.$store.getters['additionalForm/getInsuredEmailOptions']?.InsuredEmailAddress ?? null,
            InsuredEmailAttestationText: null,
            InsuredReceivesPolicyEmails: false
          };
        }
      }
    },
    policy: {
    deep: true,
    immediate: true,
    handler (newVal) {
      const emailOptions = newVal?.Policy?.Insured?.InsuredEmailOptions;

      if (emailOptions) {
        this.insuredEmailOptions = {
          InsuredEmailAddress: emailOptions.InsuredEmailAddress ?? null,
          InsuredReceivesPolicyEmails: emailOptions.InsuredReceivesPolicyEmails ?? false,
          InsuredEmailAttestationText: emailOptions.InsuredEmailAttestationText ?? null
        };
        this.canEditEmail = true;
      }
    }
  }
  }
};
</script>

<style scoped lang="scss">
@import '@/style.scss';

.risk-type {
    color: $amwins-blue;
    font-weight: bold;
}

.insured-details, .risk-details {
    h3 { color: $amwins-blue;}
}

.action-buttons {
  margin-top: 10px;
}

.blue-link {
  cursor: pointer;
}

.unsubscribe {
  text-decoration: underline !important;
  margin-top: 3%;
  margin-bottom: 3%;
  padding-left: 4%;
  border-left: solid lightgray;
}
</style>
